import { useEffect } from 'react';
import StepProps from '../StepProps';
import type { IdentifyExperienceFormValues } from './IdentifyExperienceStep.schema';
import IdentifyExperienceStepForm from './IdentifyExperienceStepForm';
import {
    buildDefaultValuesFromContext,
    mapExperienceCriteriaToRhfCriteria,
    mapRhfCriteriaToExperienceCriteria,
} from './IdentifyExperienceStepUtils';
import { useIdentifyExperienceStepForm } from './useIdentifyExperienceStepForm';
import { ExperienceCriteria } from '@/Models/ExperienceCriteria';
import ProcessingState from '@/Models/ProcessingState';

interface IdentifyExperienceStepProps extends Omit<StepProps, 'handleNext'> {
    processingState: ProcessingState;
    experienceCriteriaProcessingState: ProcessingState;
    experienceCriteria: Array<ExperienceCriteria>;
    setExperienceCriteria: (valueSet: { experienceCriteria: Array<ExperienceCriteria> }) => void;
    resetExperienceCriteriaProcessingState: () => void;
    onSubmit: (onValid: IdentifyExperienceFormValues) => Promise<void>;
    isEditMode: boolean;
}

const IdentifyExperienceStep = ({
    activeStep,
    handleBack,
    processingState,
    experienceCriteriaProcessingState,
    experienceCriteria,
    setExperienceCriteria,
    resetExperienceCriteriaProcessingState,
    onSubmit,
    isEditMode,
}: IdentifyExperienceStepProps) => {
    const form = useIdentifyExperienceStepForm(buildDefaultValuesFromContext(experienceCriteria));
    const { reset, getValues } = form;

    useEffect(() => {
        if (experienceCriteriaProcessingState === ProcessingState.Success) {
            reset({
                criteria: experienceCriteria.map(mapExperienceCriteriaToRhfCriteria),
            });
            resetExperienceCriteriaProcessingState();
        }
    }, [experienceCriteria, experienceCriteriaProcessingState, reset, resetExperienceCriteriaProcessingState]);

    const handleBackWrapper = () => {
        if (handleBack) {
            const { criteria } = getValues();

            setExperienceCriteria({
                experienceCriteria: criteria.map(mapRhfCriteriaToExperienceCriteria),
            });
            handleBack();
        }
    };

    return (
        <div className="mx-auto w-full max-w-4xl self-center">
            <IdentifyExperienceStepForm
                form={form}
                activeStep={activeStep}
                handleBack={handleBackWrapper}
                processingState={processingState}
                experienceCriteriaProcessingState={experienceCriteriaProcessingState}
                isEditMode={isEditMode}
                onSubmit={onSubmit}
            />
        </div>
    );
};

export default IdentifyExperienceStep;
